<template>
  <div class="wizard-step-compact">
    <!-- Progressive question flow — Client → Legal Name → State. -->

    <div class="wizard-question wizard-accordion-section">
      <button
        type="button"
        class="wizard-accordion-toggle"
        :aria-expanded="openSection === 'client' ? 'true' : 'false'"
        @click="toggleSection('client')"
      >
        <h5 class="wizard-question-heading">Who is the client you want the invoice to be raised for?</h5>
        <feather-icon :icon="openSection === 'client' ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="16" />
      </button>
      <b-collapse :visible="openSection === 'client'">
      <div class="d-flex align-items-start">
        <div class="flex-grow-1">
          <v-select
            id="client-select"
            v-model="formData.client"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            label="title"
            :options="clients"
            placeholder="Select client"
            :reduce="o => o.title"
            :append-to-body="true"
            :calculate-position="placeWizardDropdown"
            @input="onClientChange"
          />
        </div>
        <b-button
          variant="outline-primary"
          size="sm"
          class="ml-1 flex-shrink-0"
          aria-label="Add Client"
          @click="$emit('add-client')"
        >
          <feather-icon icon="PlusIcon" size="14" />
        </b-button>
      </div>
      <small v-if="errors.client" class="text-danger d-block mt-25">{{ errors.client }}</small>
      </b-collapse>
    </div>

    <div class="wizard-question wizard-accordion-section" v-if="formData.client">
      <button
        type="button"
        class="wizard-accordion-toggle"
        :aria-expanded="openSection === 'legalName' ? 'true' : 'false'"
        @click="toggleSection('legalName')"
      >
        <h5 class="wizard-question-heading">Which legal name should this invoice use?</h5>
        <feather-icon :icon="openSection === 'legalName' ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="16" />
      </button>
      <b-collapse :visible="openSection === 'legalName'">
      <v-select
        id="client-legal-name-select"
        v-model="formData.invoiceClientInfo.legalName"
        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
        :options="clientLegalNameOptions"
        :disabled="clientMasterLoading || !clientLegalNameOptions.length"
        :clearable="false"
        placeholder="Select legal name"
        :append-to-body="true"
        :calculate-position="placeWizardDropdown"
        @input="onLegalNameSelected"
      />
      <small v-if="clientMasterLoading" class="wizard-question-hint">
        <b-spinner small class="mr-1" />Loading legal entities…
      </small>
      <small v-else-if="legalNameIsFallback" class="wizard-question-hint">
        No legal entity on file for this client — using the client name as the legal name.
        Use “Override client billing details” below if you need a different one.
      </small>
      <small v-else-if="clientLegalNameOptions.length > 1" class="wizard-question-hint">
        This client has multiple registered entities — pick the one to invoice.
      </small>
      <small v-if="errors.legalName" class="text-danger d-block mt-25">{{ errors.legalName }}</small>
      </b-collapse>
    </div>

    <div
      class="wizard-question wizard-accordion-section"
      v-if="formData.client && formData.invoiceClientInfo.legalName && (clientStateOptions.length || clientMasterLoading)"
    >
      <button
        type="button"
        class="wizard-accordion-toggle"
        :aria-expanded="openSection === 'state' ? 'true' : 'false'"
        @click="toggleSection('state')"
      >
        <h5 class="wizard-question-heading">Which state applies for this client?</h5>
        <feather-icon :icon="openSection === 'state' ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="16" />
      </button>
      <b-collapse :visible="openSection === 'state'">
      <v-select
        id="client-state-select"
        v-model="formData.clientState"
        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
        :options="clientStateOptions"
        :disabled="clientMasterLoading || !clientStateOptions.length"
        :clearable="false"
        placeholder="Select state"
        :append-to-body="true"
        :calculate-position="placeWizardDropdown"
        @input="$emit('client-state-selected', formData.clientState)"
      />
      <small v-if="clientMasterLoading" class="wizard-question-hint">
        <b-spinner small class="mr-1" />Loading…
      </small>
      <small v-else-if="errors.clientState" class="text-danger d-block mt-25">{{ errors.clientState }}</small>
      </b-collapse>
    </div>

    <template v-if="formData.client">
      <div class="d-flex align-items-center flex-wrap mb-1">
        <small
          v-if="formData.invoiceClientInfo.legalName && clientStateOptions.length && !formData.clientState"
          class="text-muted mr-2"
        >
          Select a state to auto-fill GST &amp; address — see the live preview for the result.
        </small>
        <b-button
          variant="link"
          size="sm"
          class="p-0 flex-shrink-0"
          @click="editClientDetails = !editClientDetails"
        >
          <feather-icon icon="Edit2Icon" size="12" class="mr-25" />{{ editClientDetails ? 'Hide billing override' : 'Override client billing details' }}
        </b-button>
      </div>
      <b-alert v-if="masterDataIncomplete" show variant="warning" class="py-1 px-2 mt-1 mb-1 small">
        <feather-icon icon="AlertCircleIcon" size="14" class="mr-1" />GST/address missing for this client — use the override above to enter it manually.
      </b-alert>
      <b-collapse v-model="editClientDetails" class="mb-1">
        <b-card class="mb-0" body-class="py-2 px-2">
          <b-form-group label="Client Name" label-class="small mb-0"><b-form-input v-model="formData.invoiceClientInfo.clientName" size="sm" /></b-form-group>
          <b-form-group label="Legal Name" label-class="small mb-0"><b-form-input v-model="formData.invoiceClientInfo.legalName" size="sm" /></b-form-group>
          <b-form-group label="GST Number" label-class="small mb-0"><b-form-input v-model="formData.invoiceClientInfo.clientGST" size="sm" /></b-form-group>
          <b-form-group label="Address" label-class="small mb-0"><b-form-textarea v-model="formData.invoiceClientInfo.clientAddress" rows="2" size="sm" /></b-form-group>
          <b-form-group label="State" label-class="small mb-0"><b-form-input v-model="formData.invoiceClientInfo.clientState" size="sm" /></b-form-group>
          <b-form-group label="Place of Supply" label-class="small mb-0"><b-form-input v-model="formData.invoiceClientInfo.placeOfSupply" size="sm" /></b-form-group>
          <b-form-group label="Contact" label-class="small mb-0"><b-form-input v-model="formData.invoiceClientInfo.clientPhone" size="sm" /></b-form-group>
        </b-card>
      </b-collapse>
    </template>

    <div class="wizard-nav-actions d-flex justify-content-between align-items-center w-100 flex-wrap">
      <b-button
        variant="outline-secondary"
        size="sm"
        class="wizard-icon-btn"
        aria-label="Back: Doc Type"
        @click="$emit('back')"
      >
        <feather-icon icon="ArrowLeftIcon" size="16" />
      </b-button>
      <b-button
        variant="primary"
        size="sm"
        class="wizard-icon-btn"
        aria-label="Next: Sattva GST / Location"
        @click="onNext"
      >
        <feather-icon icon="ArrowRightIcon" size="16" />
      </b-button>
      <small v-if="!isValid" class="text-muted d-block mt-1 w-100">{{ validationHint }}</small>
    </div>
  </div>
</template>

<script>
import {
  BCard, BFormGroup, BFormInput, BFormTextarea, BButton, BAlert,
  BSpinner, BCollapse,
} from 'bootstrap-vue'
import vSelect from 'vue-select'

export default {
  name: 'WizardClientStep',
  components: {
    BCard, BFormGroup, BFormInput, BFormTextarea, BButton, BAlert,
    BSpinner, BCollapse, vSelect,
  },
  props: {
    formData: { type: Object, required: true },
    errors: { type: Object, default: () => ({}) },
    clients: { type: Array, default: () => [] },
    clientStateOptions: { type: Array, default: () => [] },
    clientLegalNameOptions: { type: Array, default: () => [] },
    clientMasterLoading: { type: Boolean, default: false },
    clientMasterData: { type: Object, default: null },
    legalNameIsFallback: { type: Boolean, default: false },
  },
  data() {
    return {
      editClientDetails: false,
      openSection: 'client',
    }
  },
  computed: {
    isValid() {
      if (!this.formData.client) return false
      if (this.clientLegalNameOptions.length > 0 && !this.formData.invoiceClientInfo.legalName) return false
      if (this.clientStateOptions.length > 0) return !!this.formData.clientState
      return true
    },
    validationHint() {
      if (!this.formData.client) return 'Select a client to continue.'
      if (!this.formData.invoiceClientInfo.legalName) return 'Select a legal name to continue.'
      if (this.clientStateOptions.length && !this.formData.clientState) return 'Select a state to continue.'
      return ''
    },
    masterDataIncomplete() {
      const info = this.formData.invoiceClientInfo
      if (!this.formData.client || !this.formData.clientState) return false
      return !!this.clientMasterData && !!info && !info.clientGST && !info.clientAddress
    },
  },
  watch: {
    masterDataIncomplete(val) {
      if (val) this.editClientDetails = true
    },
  },
  methods: {
    toggleSection(key) {
      this.openSection = this.openSection === key ? '' : key
    },
    onClientChange() {
      this.editClientDetails = false
      this.openSection = 'legalName'
      this.$emit('client-selected', this.formData.client)
    },
    onLegalNameSelected() {
      this.openSection = 'state'
      this.$emit('legal-name-selected', this.formData.invoiceClientInfo.legalName)
    },
    onNext() {
      this.$emit('next')
    },
    placeWizardDropdown(dropdownList, component, { width, top, left }) {
      dropdownList.classList.add('wizard-vs-dropdown')
      dropdownList.style.top = top
      dropdownList.style.left = left
      dropdownList.style.width = width
      dropdownList.style.zIndex = '1060'
      dropdownList.style.maxHeight = '240px'
      dropdownList.style.overflowY = 'auto'
    },
  },
}
</script>

<style src="./wizard-step-shared.css"></style>
